import { useEffect, useRef, useState } from 'react';
import styles from './FloatAction.module.css';

/**
 * Watch one control and report when it has scrolled out of sight — the beat
 * the craps float was built on: the throw scrolls the phone up to the dice,
 * which leaves the roll button below the fold. Where IntersectionObserver is
 * missing (jsdom does no layout), nothing ever reads as offscreen.
 */
export function useOffscreenWatch<T extends HTMLElement>(active: boolean) {
  const ref = useRef<T | null>(null);
  const [offscreen, setOffscreen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!active || !el || typeof IntersectionObserver === 'undefined') {
      setOffscreen(false);
      return;
    }
    const io = new IntersectionObserver(([entry]) => {
      if (entry) setOffscreen(!entry.isIntersecting);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [active]);
  return { ref, offscreen };
}

export interface FloatActionButton {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** The quiet pill, for the move that gives ground: fold, take nothing. */
  quiet?: boolean;
}

interface FloatActionProps {
  /** True only while the watched control is offscreen and the action is live. */
  show: boolean;
  buttons: FloatActionButton[];
}

/**
 * The commit, pinned over the screen while the real control is scrolled away —
 * craps' floating roll, opened up to every table. It never carries an action
 * the felt would refuse: every guard the real button has rides along (an empty
 * slate, a spent seat, a shut window all keep it off), and an expensive move —
 * a raise, a double — is never offered on it.
 */
export function FloatAction({ show, buttons }: FloatActionProps) {
  if (!show) return null;
  return (
    <div className={styles.floatRow}>
      {buttons.map((b) => (
        <button
          key={b.label}
          type="button"
          className={b.quiet ? styles.floatQuiet : styles.floatGo}
          disabled={b.disabled}
          onClick={b.onClick}
        >
          {b.label}
        </button>
      ))}
    </div>
  );
}
